import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import type { AccountEntity } from '@actual-app/core/types/models';
import { useQuery } from '@tanstack/react-query';

import {
  cuentasDelTraspaso,
  leerCategoriasComunes,
} from '#components/mobile/inicio/cuentaComun';
import { useAccounts } from '#hooks/useAccounts';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { aqlQuery } from '#queries/aqlQuery';

import {
  cuentaAutomatica,
  cuentaDeCategoria,
  filasPorCategoria,
  MESES_CUENTA,
} from './cuentaCategoria';
import type { GastoCuentaMes } from './cuentaCategoria';
import { useCuentasElegidas } from './usePagosFijos';

const CADUCIDAD_MS = 60_000;

export type CuentasCategorias = {
  /** Cuentas abiertas del presupuesto, en el orden de la app. */
  cuentas: AccountEntity[];
  /** Ids de `cuentas`. */
  validas: ReadonlySet<string>;
  /** Filas de gasto de cada categoría (incluido el mes actual). */
  historial: ReadonlyMap<string, GastoCuentaMes[]>;
  /** `false` mientras llega el historial (entonces no se separa nada). */
  listo: boolean;
  /** Cuenta de una categoría (`null` = sin cuenta clara o «todas»). */
  cuentaDe: (categoria: string) => string | null;
  /** La que tendría en «Automática» (sin elección a mano). */
  automaticaDe: (categoria: string) => string | null;
};

/**
 * Cuenta de cada categoría de gasto, con UNA consulta agrupada por
 * categoría, cuenta y mes de los últimos `MESES_CUENTA` meses (gasto en
 * cuentas del presupuesto, sin traspasos entre ellas).
 */
export function useCuentasCategorias(mes: string): CuentasCategorias {
  const { data: todas = [] } = useAccounts();
  const cuentas = useMemo(
    () => todas.filter(c => !c.closed && !c.offbudget),
    [todas],
  );
  const validas = useMemo(() => new Set(cuentas.map(c => c.id)), [cuentas]);
  // Elección de la ficha y cuenta del pago fijo: el mismo dato.
  const { elegidas } = useCuentasElegidas();
  const [rawComunes] = useSyncedPref('cuenta-comun-categorias');
  const comunes = useMemo(
    () => new Set(leerCategoriasComunes(rawComunes)),
    [rawComunes],
  );
  const cuentaComunId = useMemo(
    () => cuentasDelTraspaso(todas).destino?.id ?? null,
    [todas],
  );

  const desde = monthUtils.subMonths(mes, MESES_CUENTA - 1);
  const { data: filas } = useQuery({
    queryKey: ['cubrir', 'cuentas-categorias', desde, mes],
    // Con una sola cuenta no hay nada que separar.
    enabled: cuentas.length > 1,
    staleTime: CADUCIDAD_MS,
    queryFn: async (): Promise<GastoCuentaMes[]> => {
      const { data } = await aqlQuery(
        q('transactions')
          .filter({
            'account.offbudget': false,
            category: { $ne: null },
            amount: { $lt: 0 },
            $or: [
              { 'payee.transfer_acct': null },
              { 'payee.transfer_acct.offbudget': true },
            ],
            $and: [
              { date: { $transform: '$month', $gte: desde } },
              { date: { $transform: '$month', $lte: mes } },
            ],
          })
          .groupBy([
            { $id: '$category' },
            { $id: '$account' },
            { $month: '$date' },
          ])
          .select([
            { categoria: { $id: '$category.id' } },
            { cuenta: { $id: '$account.id' } },
            { mes: { $month: '$date' } },
            { importe: { $sum: '$amount' } },
            { movimientos: { $count: '$id' } },
          ]),
      );
      return (data as GastoCuentaMes[]).map(f => ({
        ...f,
        importe: Math.abs(f.importe),
      }));
    },
  });

  return useMemo(() => {
    const historial = filasPorCategoria(filas ?? []);
    const cache = new Map<string, string | null>();
    return {
      cuentas,
      validas,
      historial,
      listo: cuentas.length <= 1 || filas != null,
      cuentaDe: (categoria: string) => {
        if (!cache.has(categoria)) {
          cache.set(
            categoria,
            cuentaDeCategoria({
              categoria,
              historial: historial.get(categoria) ?? [],
              elegidas,
              comunes,
              cuentaComunId,
              validas,
            }),
          );
        }
        return cache.get(categoria) ?? null;
      },
      automaticaDe: (categoria: string) =>
        cuentaAutomatica({
          categoria,
          historial: historial.get(categoria) ?? [],
          comunes,
          cuentaComunId,
          validas,
        }),
    };
  }, [cuentas, validas, filas, elegidas, comunes, cuentaComunId]);
}
